import React, { useState } from "react";

import { Alert, Box, CircularProgress, Grid, TextField } from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { OrderBetween } from "@safetyculture/s12-apis-connect-web/s12/accounts/admin/v1/white_label_pb";

import { ReplacementRow } from "./custom-labels-table-components";
import { UpdateWhiteLabelReplacement } from "api/endpoints/custom-labels";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { GrpcError } from "context/snackbar-context";
import { UrlParamTypes } from "types/org-display";

export interface CustomerLabelMoveProps {
  replacement: ReplacementRow;
  rows: ReplacementRow[];
  onCancel: (event: object, reason: string) => void;
  onComplete: () => void;
  setRows: (updatedRows: ReplacementRow[]) => void;
}

const CustomerLabelMove = ({
  replacement,
  rows,
  onCancel,
  onComplete,
  setRows,
}: CustomerLabelMoveProps) => {
  const classes = mainStyles();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [targetIndex, setTargetIndex] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<GrpcError | null>(null);

  const handleMove = async () => {
    if (targetIndex === null || targetIndex < 1 || targetIndex > rows.length) {
      setError({ message: "Invalid target index." } as GrpcError);
      return;
    }

    const currentIndex = rows.findIndex((row) => row.id === replacement.id);
    if (currentIndex === -1) {
      setError({ message: "Replacement not found in the list." } as GrpcError);
      return;
    }

    let adjustedTargetIndex = targetIndex - 1;

    // Adjust the target index if moving downwards
    if (adjustedTargetIndex > currentIndex) {
      adjustedTargetIndex += 1;
    }

    const prevKey = rows[adjustedTargetIndex - 1]?.orderKey || "";
    const nextKey = rows[adjustedTargetIndex]?.orderKey || "";

    try {
      setIsProcessing(true);
      setError(null);

      await UpdateWhiteLabelReplacement(
        {
          orgId: roleId,
          id: replacement.id,
          searchValue: replacement.searchValue,
          replaceValue: replacement.replaceValue,
          order: new OrderBetween({ prevKey, nextKey }),
        },
        callOptionsHeaders(regionCode, roleId),
      );

      const updatedRows = [...rows];
      const [removed] = updatedRows.splice(currentIndex, 1);
      updatedRows.splice(adjustedTargetIndex, 0, removed);

      setRows(updatedRows);

      onComplete();
    } catch (e) {
      setError(e as GrpcError);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderErrorMessage = () => {
    if (!error) {
      return null;
    }
    return (
      <Alert severity="error" variant="filled" data-test-id="error-message">
        {error.message}
      </Alert>
    );
  };

  return (
    <ConfirmDialog
      title="Move Customer Label"
      cancelBtnLabel="Cancel"
      confirmBtnLabel="Move"
      open={!!replacement}
      setOpen={() => {}}
      onConfirm={handleMove}
      onCancel={() => onCancel({}, "cancel")}
      disabled={isProcessing}
      disableCancelBtn={isProcessing}
    >
      {isProcessing ? (
        <Grid
          container
          justifyContent="center"
          className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
        >
          <CircularProgress
            color="primary"
            variant="indeterminate"
            thickness={4}
            size={30}
            data-testid="loading-icon"
          />
        </Grid>
      ) : (
        <>
          <Box
            sx={{ width: "100%", paddingTop: "0.5rem" }}
            className={clsx(classes.spaceTopSmall)}
          >
            {/* Display searchValue */}
            <TextField
              inputProps={{
                "data-testid": "current-search-value",
              }}
              fullWidth
              margin="dense"
              label="Search Value"
              variant="outlined"
              value={replacement.searchValue}
              disabled
            />

            {/* Display replaceValue */}
            <TextField
              inputProps={{
                "data-testid": "current-replace-value",
              }}
              fullWidth
              margin="dense"
              label="Replace Value"
              variant="outlined"
              value={replacement.replaceValue}
              disabled
            />

            {/* Display currentIndex */}
            <TextField
              inputProps={{
                "data-testid": "current-index",
              }}
              fullWidth
              margin="dense"
              label="Current Index"
              variant="outlined"
              value={rows.findIndex((row) => row.id === replacement.id) + 1} // 1-based index
              disabled
            />

            {/* Input for targetIndex */}
            <TextField
              inputProps={{
                "data-testid": "target-index-input",
              }}
              fullWidth
              margin="dense"
              label="Enter Target Index"
              variant="outlined"
              value={targetIndex ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                if (
                  (/^\d*$/.test(value) && Number(value) > 0) ||
                  value === ""
                ) {
                  setTargetIndex(value === "" ? null : Number(value));
                }
              }}
              disabled={isProcessing}
            />
          </Box>
          <Box
            sx={{ width: "100%", paddingTop: "0.5rem" }}
            className={clsx(classes.spaceTopSmall)}
          >
            {renderErrorMessage()}
          </Box>
        </>
      )}
    </ConfirmDialog>
  );
};

export default CustomerLabelMove;
